import React, { ReactElement, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { User, X } from 'lucide-react';

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { ClientAccessRole, useAddClientCompanyPerson } from '@data';

import { RoleSelect } from '../../clients/clientsList/ClientAccessPanel';

/** One recipient of the send, as this dialog asks about them. */
export interface Recipient {
  email: string;
  firstName: string;
  lastName: string;
  role: ClientAccessRole | 'super';
  productIds: string[];
}

export const recipientsFrom = (emails: string[], productIds: string[]): Recipient[] =>
  emails.map(email => ({ email, firstName: '', lastName: '', role: ClientAccessRole.read, productIds }));

/** How long a removed card takes to leave. Matches the `duration-300` on its exit. */
const LEAVE_MS = 300;

/**
 * Offered straight after a send: give the people we just mailed an account at the client.
 *
 * Sending a report already creates a client account for each address — that is what the mailed link signs
 * them in as — but one with no standing anywhere, so all they can ever read is the reports they are sent.
 * This is where that becomes a decision instead of a side effect.
 *
 * The address is shown and cannot be edited. It is the one the report went to, so changing it here would
 * put a different person on the client than the one who received the mail. Everything else is open: the
 * name we do not have yet, what they may read, and what they may do.
 */
const AddRecipientsToClientDialog = ({
  organizationId,
  clientCompanyId,
  clientName,
  products,
  recipients: initialRecipients,
  onDone,
}: {
  organizationId: string;
  clientCompanyId: string;
  clientName: string;
  /** The client's products, which is all these people can be given. */
  products: { id: string; name: string }[];
  recipients: Recipient[];
  onDone: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [recipients, setRecipients] = useState<Recipient[]>(initialRecipients);
  // Rows on their way out, held for the length of the animation that takes them there.
  const [leaving, setLeaving] = useState<string[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const { mutateAsync: addPerson, isPending } = useAddClientCompanyPerson(organizationId, clientCompanyId);

  const options = products.map(product => ({ value: product.id, label: product.name }));
  const allIds = products.map(product => product.id);
  const holdsEverything = (ids: string[]): boolean => allIds.length > 0 && ids.length === allIds.length;

  const update = (email: string, change: Partial<Recipient>): void =>
    setRecipients(current => current.map(person => (person.email === email ? { ...person, ...change } : person)));

  /*
   * Taking somebody off the list plays out before the row goes: a row that vanished under the cursor left
   * the others jumping up into its place, and on a list of three that is hard to read as "that one is
   * gone" rather than "something moved". The row is only really dropped once it has left.
   */
  const drop = (email: string): void => {
    setLeaving(current => [...current, email]);
    window.setTimeout(() => {
      setRecipients(current => current.filter(person => person.email !== email));
      setLeaving(current => current.filter(item => item !== email));
    }, LEAVE_MS);
  };

  // An account carries a name, and the API insists on the first of them. Somebody who should not be at the
  // client at all comes off the list instead.
  const missingName = recipients.some(person => !person.firstName.trim());

  /*
   * One at a time and in order, so a refusal names the person it was about. Whoever came before it is
   * already added and comes off the list, which is what lets a second press carry on where it stopped
   * rather than asking the API to add them twice.
   */
  const submit = async (): Promise<void> => {
    setFailed(null);
    const queue = recipients;

    const addFrom = async (index: number): Promise<void> => {
      if (index >= queue.length) {
        onDone();
        return;
      }
      const person = queue[index];
      try {
        await addPerson({
          firstName: person.firstName.trim(),
          lastName: person.lastName.trim(),
          email: person.email,
          role: person.role === 'super' ? ClientAccessRole.admin : person.role,
          ...(person.role === 'super' ? { isSuperAdmin: true } : {}),
          productIds: person.productIds,
        });
      } catch {
        setFailed(person.email);
        setRecipients(queue.slice(index));
        return;
      }
      await addFrom(index + 1);
    };

    await addFrom(0);
  };

  /** A labelled field, so every control in the card says what it is rather than only what it holds. */
  const field = (label: string, control: ReactElement): ReactElement => (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {control}
    </div>
  );

  const row = (person: Recipient, index: number): ReactElement => (
    <div
      key={person.email}
      data-testid="recipient-card"
      /*
       * One card per person, rather than four controls on a line. Boxed, the X belongs visibly to the
       * address above it — loose on a row it read as a delete button for the dialog, and "what am I
       * removing" is not a question a remove control should raise.
       *
       * Arrives with the same swipe the guided add and the welcome use between their steps, and a dropped
       * card leaves back the way it came.
       */
      className={cx(
        'rounded-lg border border-border bg-muted/30 p-3 duration-300 ease-out motion-reduce:animate-none',
        leaving.includes(person.email)
          ? 'animate-out fade-out-0 slide-out-to-right-6'
          : 'animate-in fade-in-0 slide-in-from-right-6',
      )}
      style={leaving.includes(person.email) ? undefined : { animationDelay: `${Math.min(index, 5) * 0.06}s` }}
    >
      {/* The address heads its own card. It is the one thing here that cannot be changed, and the one
          thing that says which person the card is about, so it gets the line to itself. */}
      <div className="flex items-center gap-2.5 border-b border-border pb-2.5">
        {/* A person, not an envelope: the card is somebody being given an account, and the address is only
            how we reach them. */}
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <User className="size-4" />
        </span>
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{person.email}</p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => drop(person.email)}
          aria-label={`${t('actions.remove')} ${person.email}`}
          className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </Button>
      </div>

      {/* Who they are, then what they may see: two pairs, in that order, on one row when there is room. */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {field(
          t('firstName'),
          <Input
            value={person.firstName}
            onChange={event => update(person.email, { firstName: event.target.value })}
            placeholder={t('firstName')}
            aria-label={`${t('firstName')} ${person.email}`}
            className="h-9 bg-popover"
          />,
        )}
        {field(
          t('lastName'),
          <Input
            value={person.lastName}
            onChange={event => update(person.email, { lastName: event.target.value })}
            placeholder={t('lastName')}
            aria-label={`${t('lastName')} ${person.email}`}
            className="h-9 bg-popover"
          />,
        )}
        <MultiSelectField
          label={<span className="text-xs font-medium text-muted-foreground">{t('access.canView')}</span>}
          options={options}
          value={person.productIds}
          onChange={ids => update(person.email, { productIds: ids })}
          allSelected={holdsEverything(person.productIds)}
          onToggleAll={next => update(person.email, { productIds: next ? allIds : [] })}
          emptyLabel={t('noProducts')}
          summaryThreshold={0}
          renderCountSummary={count =>
            holdsEverything(person.productIds) ? t('allProducts') : t('access.nProducts', { count })
          }
          selectAllLabel={t('allProducts')}
          className="min-w-0 gap-1.5"
        />
        {field(
          t('access.role'),
          <RoleSelect
            ariaLabel={t('a11y.roleFor', { email: person.email })}
            className="w-full"
            // The client's top account is the guided add's to hand out, not this prompt's: it is offered
            // while setting a client up, where somebody is deciding who runs the relationship.
            offerSuper={false}
            value={person.role}
            onChange={next => update(person.email, { role: next })}
          />,
        )}
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={open => !open && onDone()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('access.addToClientTitle', { client: clientName })}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{t('access.addToClientHint')}</p>

        {/* Scrolls rather than growing past the screen: a report can go to more people than fit, and the
            footer's two answers have to stay reachable. `overflow-x-hidden` keeps the cards' swipe inside
            the box instead of widening it. */}
        <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto overflow-x-hidden px-0.5 py-0.5">
          {recipients.map((person, index) => row(person, index))}
        </div>

        {failed && <p className="mt-2 text-sm text-destructive">{t('access.addToClientFailed', { email: failed })}</p>}

        {/* An account has to carry a first name, and the send had no name to give it — so this is where
            one arrives. Said out loud rather than left as a button that will not press. */}
        {missingName && <p className="mt-2 text-sm text-muted-foreground">{t('access.addToClientNeedsName')}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onDone} disabled={isPending}>
            {t('actions.notNow')}
          </Button>
          <Button type="button" onClick={submit} disabled={isPending || recipients.length === 0 || missingName}>
            {t('access.addToClient', { count: recipients.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddRecipientsToClientDialog;
